import { useQuery } from '@tanstack/react-query';
import { getTokenFromStorage } from '@/utils/auth';
import { getCampaignDrafts } from '@/api/beatlyApi';

export interface UseDraftsParams {
  campaignId: string | undefined;
  enabled?: boolean;
}

/** API expects campaign IDs like "123456-cpg"; avoid calling with invalid ids to prevent 404/500. */
function isValidCampaignId(id: string | undefined): boolean {
  return Boolean(id && id.length >= 8 && id.includes('-'));
}

/**
 * Fetches drafts (content approval) for a campaign.
 * GET /app/v2/client/campaign/:campaignId/drafts
 */
export function useDrafts({ campaignId, enabled = true }: UseDraftsParams) {
  const token = getTokenFromStorage();
  const shouldFetch = enabled && Boolean(token && campaignId) && isValidCampaignId(campaignId);

  const { data, isLoading, isFetching, isFetched, error, isError, refetch } = useQuery({
    queryKey: ['client', 'campaign', campaignId, 'drafts'],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID is required');
      return getCampaignDrafts(campaignId, token ?? '');
    },
    enabled: shouldFetch,
    retry: false,
  });

  return {
    data: data ?? [],
    isLoading,
    isFetching,
    isFetched,
    error,
    isError,
    refetch,
  };
}
